import { useEffect, useRef } from "react";

const lerp = (a, b, t) => a + t * (b - a);
const fade = (t) => t * t * t * (t * (t * 6 - 15) + 10);
const safeR = (v) => Math.max(0.001, v); // prevents negative radius errors

const makeNoise = () => {
  const p = new Uint8Array(512);
  for (let i = 0; i < 256; i++) p[i] = i;
  for (let i = 255; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [p[i], p[j]] = [p[j], p[i]];
  }
  for (let i = 0; i < 256; i++) p[256 + i] = p[i];
  const grad = (h, x, y) => {
    const v = h & 3;
    const u = v < 2 ? x : y;
    const w = v < 2 ? y : x;
    return (v & 1 ? -u : u) + (v & 2 ? -w : w);
  };
  return (x, y) => {
    const X = Math.floor(x) & 255,
      Y = Math.floor(y) & 255;
    x -= Math.floor(x);
    y -= Math.floor(y);
    const u = fade(x),
      v = fade(y);
    const a = p[X] + Y,
      b = p[X + 1] + Y;
    return lerp(
      lerp(grad(p[a], x, y), grad(p[b], x - 1, y), u),
      lerp(grad(p[a + 1], x, y - 1), grad(p[b + 1], x - 1, y - 1), u),
      v,
    );
  };
};

// ══════════════════════════════════════════════════════════════════════════════
// STEP 1 — PROFILO: costellazione di particelle che reagiscono al mouse
// ══════════════════════════════════════════════════════════════════════════════
const ConstellationBg = ({ mousePos }) => {
  const canvasRef = useRef();
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let animId,
      t = 0;
    const noise = makeNoise();

    const resize = () => {
      canvas.width = canvas.offsetWidth * window.devicePixelRatio;
      canvas.height = canvas.offsetHeight * window.devicePixelRatio;
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    };
    resize();
    window.addEventListener("resize", resize);

    const W = () => canvas.offsetWidth,
      H = () => canvas.offsetHeight;
    const N = 80;
    const pts = Array.from({ length: N }, (_, i) => ({
      x: Math.random(),
      y: Math.random(),
      vx: (Math.random() - 0.5) * 0.0002,
      vy: (Math.random() - 0.5) * 0.0002,
      r: 1 + Math.random() * 1.5,
      phase: Math.random() * Math.PI * 2,
    }));

    const draw = () => {
      const w = W(),
        h = H();
      if (w < 1 || h < 1) {
        animId = requestAnimationFrame(draw);
        return;
      }
      ctx.clearRect(0, 0, w, h);
      const mx = mousePos.current.x,
        my = mousePos.current.y;

      pts.forEach((pt) => {
        pt.x += pt.vx + Math.sin(t * 0.3 + pt.phase) * 0.0001;
        pt.y += pt.vy + Math.cos(t * 0.25 + pt.phase) * 0.0001;
        if (pt.x < 0) pt.x = 1;
        if (pt.x > 1) pt.x = 0;
        if (pt.y < 0) pt.y = 1;
        if (pt.y > 1) pt.y = 0;
        const dx = pt.x - mx,
          dy = pt.y - my;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < 0.15) {
          pt.x += dx * 0.003;
          pt.y += dy * 0.003;
        }
      });

      // Lines between close particles
      for (let i = 0; i < N; i++) {
        for (let j = i + 1; j < N; j++) {
          const dx = (pts[i].x - pts[j].x) * w,
            dy = (pts[i].y - pts[j].y) * h;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < 120) {
            ctx.strokeStyle = `rgba(245,197,24,${(1 - d / 120) * 0.12})`;
            ctx.lineWidth = 0.5;
            ctx.beginPath();
            ctx.moveTo(pts[i].x * w, pts[i].y * h);
            ctx.lineTo(pts[j].x * w, pts[j].y * h);
            ctx.stroke();
          }
        }
        // Mouse connection
        const mdx = (pts[i].x - mx) * w,
          mdy = (pts[i].y - my) * h;
        const md = Math.sqrt(mdx * mdx + mdy * mdy);
        if (md < 180) {
          ctx.strokeStyle = `rgba(245,197,24,${(1 - md / 180) * 0.3})`;
          ctx.lineWidth = 0.8;
          ctx.beginPath();
          ctx.moveTo(pts[i].x * w, pts[i].y * h);
          ctx.lineTo(mx * w, my * h);
          ctx.stroke();
        }
      }

      // Dots
      pts.forEach((pt) => {
        const mdx = (pt.x - mx) * w,
          mdy = (pt.y - my) * h;
        const md = Math.sqrt(mdx * mdx + mdy * mdy);
        const bright = Math.max(0, 1 - md / 200);
        const alpha = 0.3 + Math.sin(t * 0.8 + pt.phase) * 0.15 + bright * 0.4;
        ctx.fillStyle = `rgba(245,197,24,${alpha})`;
        ctx.beginPath();
        ctx.arc(pt.x * w, pt.y * h, pt.r + bright * 2, 0, Math.PI * 2);
        ctx.fill();
        if (bright > 0.4) {
          ctx.fillStyle = `rgba(255,255,255,${bright * 0.5})`;
          ctx.beginPath();
          ctx.arc(pt.x * w, pt.y * h, pt.r * 0.4, 0, Math.PI * 2);
          ctx.fill();
        }
      });

      t += 0.016;
      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", resize);
    };
  }, []);
  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
      }}
    />
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// STEP 2 — ESPERIENZA: onde audio/sonore che pulsano
// ══════════════════════════════════════════════════════════════════════════════
const WavesBg = ({ mousePos }) => {
  const canvasRef = useRef();
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let animId,
      t = 0;
    const noise = makeNoise();
    const resize = () => {
      canvas.width = canvas.offsetWidth * window.devicePixelRatio;
      canvas.height = canvas.offsetHeight * window.devicePixelRatio;
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    };
    resize();
    window.addEventListener("resize", resize);
    const W = () => canvas.offsetWidth,
      H = () => canvas.offsetHeight;

    const draw = () => {
      const w = W(),
        h = H();
      ctx.clearRect(0, 0, w, h);
      const mx = mousePos.current.x * w,
        my = mousePos.current.y * h;
      const WAVES = 7;

      for (let wi = 0; wi < WAVES; wi++) {
        const progress = wi / WAVES;
        const alpha =
          (0.04 + progress * 0.06) * (1 + Math.sin(t * 0.5 + wi) * 0.2);
        const amp = (30 + wi * 18) * (1 + noise(wi * 0.5, t * 0.2) * 0.3);
        const freq = 0.008 - wi * 0.0005;
        const speed = t * (0.4 + wi * 0.12);
        const yBase = h * (0.2 + progress * 0.65);

        // Mouse influence — waves bend toward cursor
        const mouseInfluence = Math.max(
          0,
          1 - Math.abs(my - yBase) / (h * 0.3),
        );

        ctx.beginPath();
        for (let x = 0; x <= w; x += 3) {
          const distToMouse = Math.abs(x - mx) / w;
          const mPull = mouseInfluence * Math.max(0, 1 - distToMouse * 3) * 40;
          const y =
            yBase +
            Math.sin(x * freq + speed) * amp +
            Math.sin(x * freq * 1.7 + speed * 0.7) * amp * 0.4 +
            noise(x * 0.005, t * 0.15 + wi) * amp * 0.5 -
            mPull;
          if (x === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.lineTo(w, h);
        ctx.lineTo(0, h);
        ctx.closePath();

        const grad = ctx.createLinearGradient(0, yBase - amp, 0, yBase + amp);
        grad.addColorStop(0, `rgba(245,197,24,${alpha * 1.5})`);
        grad.addColorStop(0.5, `rgba(255,100,30,${alpha})`);
        grad.addColorStop(1, "rgba(0,0,0,0)");
        ctx.fillStyle = grad;
        ctx.fill();
      }

      // Vertical scan lines (subtle)
      for (let x = 0; x < w; x += 80) {
        ctx.strokeStyle = `rgba(245,197,24,0.03)`;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, h);
        ctx.stroke();
      }

      t += 0.016;
      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", resize);
    };
  }, []);
  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
      }}
    />
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// STEP 3 — FORMAZIONE: griglia morfante isometrica
// ══════════════════════════════════════════════════════════════════════════════
const GridMorphBg = ({ mousePos }) => {
  const canvasRef = useRef();
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let animId,
      t = 0;
    const noise = makeNoise();
    const resize = () => {
      canvas.width = canvas.offsetWidth * window.devicePixelRatio;
      canvas.height = canvas.offsetHeight * window.devicePixelRatio;
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    };
    resize();
    window.addEventListener("resize", resize);
    const W = () => canvas.offsetWidth,
      H = () => canvas.offsetHeight;

    const draw = () => {
      const w = W(),
        h = H();
      if (w < 1 || h < 1) {
        animId = requestAnimationFrame(draw);
        return;
      }
      ctx.clearRect(0, 0, w, h);
      const mx = mousePos.current.x * w,
        my = mousePos.current.y * h;
      const STEP = 55;

      for (let x = -STEP; x < w + STEP; x += STEP) {
        for (let y = -STEP; y < h + STEP; y += STEP) {
          const n = noise(x * 0.006 + t * 0.12, y * 0.006 + t * 0.09);
          const dx = x - mx,
            dy = y - my;
          const dist = Math.sqrt(dx * dx + dy * dy);
          const pull = Math.max(0, 1 - dist / 250) * 20;
          const px = x + n * 18 + (dx / dist || 0) * pull * -1;
          const py = y + n * 14 + (dy / dist || 0) * pull * -1;
          const alpha = (0.06 + Math.abs(n) * 0.12) * (1 + pull / 30);
          const size = 1.5 + Math.abs(n) * 2 + pull * 0.08;
          ctx.fillStyle = `rgba(245,197,24,${alpha})`;
          ctx.beginPath();
          ctx.arc(px, py, size, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      // Mouse ripple rings
      const rippleR = (t % 3) * 150;
      if (rippleR > 0) {
        ctx.strokeStyle = `rgba(245,197,24,${Math.max(0, 0.15 - rippleR / 1500)})`;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.arc(mx, my, rippleR, 0, Math.PI * 2);
        ctx.stroke();
      }

      t += 0.016;
      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", resize);
    };
  }, []);
  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
      }}
    />
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// STEP 4 — COMPETENZE: blob fluido che respira
// ══════════════════════════════════════════════════════════════════════════════
const BlobBg = ({ mousePos }) => {
  const canvasRef = useRef();
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let animId,
      t = 0;
    const noise = makeNoise();
    const resize = () => {
      canvas.width = canvas.offsetWidth * window.devicePixelRatio;
      canvas.height = canvas.offsetHeight * window.devicePixelRatio;
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    };
    resize();
    window.addEventListener("resize", resize);
    const W = () => canvas.offsetWidth,
      H = () => canvas.offsetHeight;

    const BLOBS = [
      { cx: 0.35, cy: 0.5, r: 0.28, color: [245, 197, 24], speed: 0.3 },
      { cx: 0.65, cy: 0.45, r: 0.22, color: [255, 100, 30], speed: 0.4 },
      { cx: 0.5, cy: 0.65, r: 0.18, color: [100, 50, 255], speed: 0.25 },
    ];

    const draw = () => {
      const w = W(),
        h = H();
      if (w < 1 || h < 1) {
        animId = requestAnimationFrame(draw);
        return;
      }
      ctx.clearRect(0, 0, w, h);
      const mx = mousePos.current.x,
        my = mousePos.current.y;

      BLOBS.forEach((blob, bi) => {
        const wobCx =
          blob.cx +
          Math.sin(t * blob.speed + bi * 2.1) * 0.06 +
          (mx - 0.5) * 0.04;
        const wobCy =
          blob.cy +
          Math.cos(t * blob.speed * 0.8 + bi * 1.7) * 0.04 +
          (my - 0.5) * 0.03;
        const cx = wobCx * w,
          cy = wobCy * h;
        const r = safeR(blob.r * Math.min(w, h));
        const POINTS = 24;
        ctx.beginPath();
        for (let i = 0; i <= POINTS; i++) {
          const angle = (i / POINTS) * Math.PI * 2;
          const nVal = noise(
            Math.cos(angle) * 2 + t * 0.2 + bi,
            Math.sin(angle) * 2 + t * 0.15 + bi,
          );
          const rr = safeR(r * (1 + nVal * 0.25));
          const px = cx + Math.cos(angle) * rr;
          const py = cy + Math.sin(angle) * rr * 0.85;
          if (i === 0) ctx.moveTo(px, py);
          else ctx.lineTo(px, py);
        }
        ctx.closePath();
        const [r1, g1, b1] = blob.color;
        const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
        grad.addColorStop(0, `rgba(${r1},${g1},${b1},0.12)`);
        grad.addColorStop(0.5, `rgba(${r1},${g1},${b1},0.06)`);
        grad.addColorStop(1, `rgba(${r1},${g1},${b1},0)`);
        ctx.fillStyle = grad;
        ctx.fill();
      });

      // Floating micro-particles
      for (let i = 0; i < 30; i++) {
        const n1 = noise(i * 0.4, t * 0.1);
        const n2 = noise(i * 0.4 + 10, t * 0.1);
        const px = (0.2 + n1 * 0.6) * w;
        const py = (0.2 + n2 * 0.6) * h;
        const alpha = 0.1 + Math.abs(noise(i, t * 0.3)) * 0.2;
        ctx.fillStyle = `rgba(245,197,24,${alpha})`;
        ctx.beginPath();
        ctx.arc(px, py, 1 + Math.abs(n1) * 2, 0, Math.PI * 2);
        ctx.fill();
      }

      t += 0.016;
      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", resize);
    };
  }, []);
  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
      }}
    />
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// STEP 5 — DOWNLOAD: vortice/warp come spaceship
// ══════════════════════════════════════════════════════════════════════════════
const VortexBg = ({ mousePos }) => {
  const canvasRef = useRef();
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let animId,
      t = 0;
    const noise = makeNoise();
    const resize = () => {
      canvas.width = canvas.offsetWidth * window.devicePixelRatio;
      canvas.height = canvas.offsetHeight * window.devicePixelRatio;
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    };
    resize();
    window.addEventListener("resize", resize);
    const W = () => canvas.offsetWidth,
      H = () => canvas.offsetHeight;
    const LINES = 60;
    const pts = Array.from({ length: LINES }, (_, i) => ({
      angle: (i / LINES) * Math.PI * 2,
      speed: 0.008 + Math.random() * 0.006,
      r: 0.3 + Math.random() * 0.6,
      len: 0.05 + Math.random() * 0.12,
      phase: Math.random() * Math.PI * 2,
    }));

    const draw = () => {
      const w = W(),
        h = H();
      if (w < 1 || h < 1) {
        animId = requestAnimationFrame(draw);
        return;
      }
      ctx.clearRect(0, 0, w, h);
      const mx = mousePos.current.x * w,
        my = mousePos.current.y * h;
      const cx = w * 0.5 + (mousePos.current.x - 0.5) * 40;
      const cy = h * 0.45 + (mousePos.current.y - 0.5) * 30;
      const minDim = safeR(Math.min(w, h));

      // Center glow
      const cg = ctx.createRadialGradient(cx, cy, 0, cx, cy, minDim * 0.25);
      cg.addColorStop(0, "rgba(245,197,24,0.08)");
      cg.addColorStop(0.4, "rgba(245,100,20,0.04)");
      cg.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = cg;
      ctx.beginPath();
      ctx.ellipse(
        cx,
        cy,
        safeR(minDim * 0.25),
        safeR(minDim * 0.2),
        0,
        0,
        Math.PI * 2,
      );
      ctx.fill();

      pts.forEach((pt) => {
        pt.angle += pt.speed;
        const wobble = noise(pt.angle * 0.5, t * 0.2 + pt.phase) * 0.15;
        const r = (pt.r + wobble) * Math.min(w, h) * 0.45;
        const x1 = cx + Math.cos(pt.angle) * r;
        const y1 = cy + Math.sin(pt.angle) * r * 0.7;
        const x2 = cx + Math.cos(pt.angle + pt.len) * r * (1 - pt.len * 0.3);
        const y2 =
          cy + Math.sin(pt.angle + pt.len) * r * 0.7 * (1 - pt.len * 0.3);

        const distToMouse = Math.sqrt((x1 - mx) ** 2 + (y1 - my) ** 2);
        const brightBoost = Math.max(0, 1 - distToMouse / 200);
        const alpha =
          (0.08 + pt.r * 0.12 + brightBoost * 0.3) *
          (1 + Math.sin(t * 0.5 + pt.phase) * 0.2);

        const heat = pt.r;
        ctx.strokeStyle = `rgba(${Math.floor(245 - heat * 50)},${Math.floor(197 - heat * 100)},${Math.floor(24 + heat * 20)},${alpha})`;
        ctx.lineWidth = 0.8 + brightBoost * 1.5;
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.stroke();
      });

      t += 0.016;
      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", resize);
    };
  }, []);
  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
      }}
    />
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// EXPORT — wrapper universale
// ══════════════════════════════════════════════════════════════════════════════
const BG_MAP = {
  1: ConstellationBg,
  2: WavesBg,
  3: GridMorphBg,
  4: BlobBg,
  5: VortexBg,
};

const AnimatedBackground = ({ step, mousePos }) => {
  const BgComponent = BG_MAP[step];
  if (!BgComponent) return null;
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 0,
        background: "#080909",
        overflow: "hidden",
      }}
    >
      <BgComponent mousePos={mousePos} />
      {/* Consistent vignette over every step */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          background:
            "radial-gradient(ellipse at 50% 50%, transparent 40%, rgba(8,9,9,0.75) 100%)",
        }}
      />
    </div>
  );
};

export default AnimatedBackground;
